import { forwardRef, useEffect, useState, type FocusEvent } from 'react';
import { cn } from '@/lib/utils';
import { normalizeTime24h } from '@/lib/datetime';
import { Input, type InputProps } from './input';

// TimeInput24h renders a 24h HH:MM time field. We avoid `<input type="time">`
// because the browser's locale leaks through (en-US users see AM/PM, sv-SE
// users see 24h, etc.), and SportAdmin's whole product is 24h — schedule
// templates, activities, availability, all of it. Mixing formats inside the
// same screen is confusing, and there's no HTML attribute that forces a
// locale-independent native picker. So instead of native, we use a
// text input with numeric input-mode + a pattern so mobile pops the
// number keypad and the stored value is always "HH:MM".
//
// Behaviour:
//   - Value MUST be 24h "HH:MM" (e.g. "17:30") or empty. Anything else is
//     rejected by the pattern; on blur the field auto-pads a one-digit
//     hour ("9:0" → "09:00"). The onChange contract matches the native
//     <input type="time"> — callers don't notice the swap.
//   - inputMode="numeric" + `pattern` triggers a numeric on-screen keyboard
//     on iOS/Android while still allowing the colon character (typed via
//     long-press or the symbol key).
//   - On blur, if the field is non-empty but invalid, we leave it alone
//     and the surrounding form's validation surfaces the error — same
//     contract a native time input has.

export interface TimeInput24hProps
  extends Omit<InputProps, 'type' | 'value' | 'onChange'> {
  value: string;
  onChange: (next: string) => void;
}

export const TimeInput24h = forwardRef<HTMLInputElement, TimeInput24hProps>(
  ({ value, onChange, onBlur, className, ...props }, ref) => {
    // Mirror prop value in local state so the user can type freely before
    // we normalize on blur. Keep them in sync if the parent updates.
    const [internal, setInternal] = useState(value);
    useEffect(() => {
      setInternal(value);
    }, [value]);

    return (
      <Input
        ref={ref}
        type="text"
        inputMode="numeric"
        // Pattern doubles as a hint for assistive tech + native HTML
        // constraint validation when the field is wrapped in a <form>.
        pattern="([01][0-9]|2[0-3]):[0-5][0-9]"
        maxLength={5}
        placeholder="HH:MM"
        autoComplete="off"
        spellCheck={false}
        className={cn('tabular-nums', className)}
        value={internal}
        onChange={(e) => {
          setInternal(e.target.value);
          // Forward immediately so React Query / form state stays live —
          // matches native <input type="time"> change semantics.
          onChange(e.target.value);
        }}
        onBlur={(e: FocusEvent<HTMLInputElement>) => {
          const normalized = normalizeTime24h(e.target.value);
          if (normalized !== e.target.value) {
            setInternal(normalized);
            onChange(normalized);
          }
          onBlur?.(e);
        }}
        {...props}
      />
    );
  },
);
TimeInput24h.displayName = 'TimeInput24h';
